<template>
	<b-card no-body class="dashboard-panel d-flex flex-column h-100" :class="{ 'edit-mode': editMode }">
		<div class="panel-header d-flex align-items-center" @contextmenu.prevent="openMenu">
			<span class="panel-header-side"></span>
			<span class="panel-title flex-fill text-truncate text-center">
				<slot name="title">{{ title }}</slot>
			</span>
			<span class="panel-header-side justify-content-end">
			<b-dropdown v-if="hasMenu" ref="menu" right no-caret variant="link" size="sm" toggle-class="panel-menu-btn p-0" class="panel-menu" title="Panel options" @shown="positionMenu" @hidden="ctxMenu = null">
				<template #button-content><b-icon icon="three-dots-vertical"></b-icon></template>
				<slot name="menu"></slot>
				<template v-if="popoutUrl">
					<b-dropdown-divider v-if="hasMenuSlot"></b-dropdown-divider>
					<b-dropdown-item @click="popOut"><b-icon icon="box-arrow-up-right" class="mr-2"></b-icon>Pop out window</b-dropdown-item>
					<b-dropdown-item @click="copyDockUrl"><b-icon icon="clipboard" class="mr-2"></b-icon>Copy browser dock URL</b-dropdown-item>
				</template>
				<template v-if="editMode">
					<b-dropdown-divider v-if="popoutUrl || hasMenuSlot"></b-dropdown-divider>
					<b-dropdown-item @click="$emit('remove')"><b-icon icon="x-lg" class="mr-2"></b-icon>Remove panel</b-dropdown-item>
				</template>
			</b-dropdown>
			</span>
		</div>
		<div class="dashboard-panel-body flex-fill d-flex flex-column overflow-auto">
			<slot></slot>
		</div>
	</b-card>
</template>

<script>
export default {
	name: 'DashboardPanel',
	props: {
		title: { type: String, default: '' },
		editMode: { type: Boolean, default: false },
		// Path (e.g. '/app/chat.html') to a standalone popout page, or null for no popout control.
		popoutUrl: { type: String, default: null },
		popoutWidth: { type: Number, default: 400 },
		popoutHeight: { type: Number, default: 700 },
	},
	data() { return {
		// { x, y } cursor position when the menu was opened via right-click, else null.
		ctxMenu: null,
	}},
	computed: {
		hasMenuSlot() { return !!this.$slots.menu; },
		hasMenu() { return this.editMode || !!this.popoutUrl || this.hasMenuSlot; },
		absolutePopoutUrl() {
			if(!this.popoutUrl) return null;
			return /^https?:/.test(this.popoutUrl) ? this.popoutUrl : `${this.$baseUrl}${this.popoutUrl}`;
		},
	},
	methods: {
		// Right-clicking the title bar opens the panel's ⋮ menu at the cursor.
		openMenu(e) {
			if(!this.hasMenu || !this.$refs.menu) return;
			this.ctxMenu = { x: e.clientX, y: e.clientY };
			this.$refs.menu.show();
		},
		// When opened from a right-click, place the menu at the cursor. Grid items use CSS
		// transforms, so a fixed-positioned child is offset from the item's box, not the viewport.
		positionMenu() {
			if(!this.ctxMenu) return;
			const menuEl = this.$refs.menu && this.$refs.menu.$el && this.$refs.menu.$el.querySelector('.dropdown-menu');
			if(!menuEl) return;
			const base = (this.$el.closest && this.$el.closest('.vue-grid-item')) || this.$el;
			const rect = base.getBoundingClientRect();
			menuEl.style.setProperty('position', 'fixed', 'important');
			menuEl.style.setProperty('transform', 'none', 'important');
			menuEl.style.setProperty('margin', '0', 'important');
			menuEl.style.setProperty('left', `${this.ctxMenu.x - rect.left}px`, 'important');
			menuEl.style.setProperty('top', `${this.ctxMenu.y - rect.top}px`, 'important');
		},
		popOut() {
			window.open(this.absolutePopoutUrl, 'winname', `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=${this.popoutWidth},height=${this.popoutHeight}`);
		},
		copyDockUrl() {
			this.$util.copyToClipboard(this.absolutePopoutUrl);
			this.$bvToast.toast('Browser dock URL copied — paste it into an OBS custom browser dock', { title: 'Copied', variant: 'success', solid: true });
		},
	},
};
</script>

<style>
.dashboard-panel {
	/* Card must not clip, so the ⋮ menu isn't cut off by the panel. */
	overflow: visible;
	border: none;
}
/* Compact, low-chrome title bar (no card-header border / padding). */
.dashboard-panel > .panel-header {
	position: relative;
	min-height: 38px;
	padding: 0.35rem 0.6rem;
	font-size: 1.1rem;
	font-weight: 500;
	line-height: 1.3;
	background-color: rgba(128, 128, 128, 0.09);
	border-top-left-radius: calc(0.25rem - 1px);
	border-top-right-radius: calc(0.25rem - 1px);
	user-select: none;
	-webkit-user-select: none;
}
/* Equal side gutters keep the title centered across the whole bar without absolute positioning
   (absolute + transform on the menu would trap its z-index and let panel content cover it). */
.dashboard-panel .panel-header-side {
	flex: 0 0 auto;
	min-width: 26px;
	display: flex;
	align-items: center;
}
.dashboard-panel > .dashboard-panel-body {
	border-bottom-left-radius: calc(0.25rem - 1px);
	border-bottom-right-radius: calc(0.25rem - 1px);
}
.dashboard-panel .panel-menu-btn {
	color: inherit;
	opacity: 0.6;
	padding: 0 0.3rem;
	cursor: pointer;
}
.dashboard-panel .panel-menu-btn:hover,
.dashboard-panel .panel-menu.show .panel-menu-btn {
	opacity: 1;
}
</style>
